import React, { Dispatch, SetStateAction, useRef } from "react";
import { useAtomValue } from "jotai/index";
import { type Device, sessionMutationAtom } from "../../utils/simulator";
import { CircularProgress } from "@mui/material";
import styled from "@emotion/styled";
import { useInteractions } from "./use-interactions-hook";
import { type CompanyData } from "../../utils/types";
import { AnnotatedScreenshot } from "@/routes/sessions/AnnotatedScreenshot";
import type { DeviceContextState } from "@/routes/test-builder-page";
import InspectorElementsWrapper from "@/components/device-provider/inspector-elements-wrapper";

const GptDriverDeviceProvider = ({
  startGptDriverSession,
  companyData,
  platform,
  deviceContextState,
  setDeviceContextState,
}: {
  startGptDriverSession: () => void;
  companyData: CompanyData;
  platform?: string;
  deviceContextState?: DeviceContextState;
  setDeviceContextState: Dispatch<SetStateAction<DeviceContextState>>;
}) => {
  const { data: gptDriverSimulatorData, isPending } =
    useAtomValue(sessionMutationAtom);

  const wrapperRef = useRef<HTMLDivElement>(null);

  // Use the interactions hook
  const { isFocused, cursorPos, trail, isTapping, hasStreamElement } =
    useInteractions({
      wrapperRef,
      companyData,
      platform,
      deviceContextState,
    });

  return (
    <AppetizeCloneDeviceWrapper
      tabIndex={0}
      ref={wrapperRef}
      id="deviceWrapper"
      device={gptDriverSimulatorData?.device}
      activeSession={gptDriverSimulatorData != null && hasStreamElement}
      isFocused={isFocused}
      onClick={
        gptDriverSimulatorData == null && !isPending
          ? startGptDriverSession
          : undefined
      }
    >
      {isPending && (
        <LoadingWrapper>
          <CircularProgress />
          <p>Emulator is launching... (about 1 min)</p>
        </LoadingWrapper>
      )}
      {gptDriverSimulatorData != null && !hasStreamElement && (
        <LoadingWrapper>
          <CircularProgress />
          <p>Setting up your app & stream... almost ready!</p>
        </LoadingWrapper>
      )}
      {!isPending && gptDriverSimulatorData == null && (
        <TapToStartButton>Tap to start</TapToStartButton>
      )}

      {/* Tap cursor */}
      {isTapping && (
        <div
          className="absolute w-8 h-8 pointer-events-none"
          style={{
            left: cursorPos.x - 16,
            top: cursorPos.y - 16,
          }}
        >
          <svg viewBox="0 0 32 32" className="w-full h-full">
            <circle
              cx="16"
              cy="16"
              r="8"
              className="fill-blue-500 opacity-30"
            />
            <circle
              cx="16"
              cy="16"
              r="6"
              className="fill-blue-500 opacity-60"
            />
          </svg>
        </div>
      )}

      {/* Trail line */}
      <TrailLineSvg device={gptDriverSimulatorData?.device}>
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#3B82F6"
              fillOpacity="0.3"
            />
          </marker>
        </defs>

        {trail != null && (
          <line
            x1={trail.startX}
            y1={trail.startY}
            x2={trail.endX}
            y2={trail.endY}
            stroke="#3B82F6"
            strokeWidth="2"
            className="opacity-30"
            markerEnd="url(#arrowhead)"
          />
        )}
      </TrailLineSvg>
      <InspectorElementsWrapper>
        <AnnotatedScreenshot
          setDeviceContextState={setDeviceContextState}
          deviceContextState={deviceContextState}
          usingGptDriverDeviceProvider={gptDriverSimulatorData != null}
        />
      </InspectorElementsWrapper>
    </AppetizeCloneDeviceWrapper>
  );
};

const AppetizeCloneDeviceWrapper = styled.div<{
  activeSession: boolean;
  isFocused: boolean;
  device?: Device;
}>`
  position: relative;
  display: grid;
  overflow: hidden;
  ${(props) =>
    props.device != null &&
    `
    display: initial;
    aspect-ratio: ${props.device.screen.width / props.device.screen.height};
    width: min-content;
    height: 100%;
    left: 50%;
    right: 50%;
    transform: translate(-50%, 0);
  `}
  border-radius: ${(props) => (props.activeSession ? "0px" : "40px")};
  ${(props) =>
    !props.activeSession &&
    `
    place-content: center;
    margin-inline: 6rem;
    background-color: rgba(76, 99, 255, 0.1);
    cursor: pointer;
    border: 2px solid #4C63FF;
  `}
`;

// Styled components
const TrailLineSvg = styled.svg<{ device?: Device }>`
  position: absolute;
  z-index: 1;
  pointer-events: none;

  ${(props) =>
    props.device != null &&
    `
    aspect-ratio: ${props.device.screen.width / props.device.screen.height};
    height: -webkit-fill-available;
    height: 100%;
    left: 50%;
    right: 50%;
    transform: translate(-50%, 0);
  `}
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 1.125rem;
  padding-inline: 2rem;
  text-align: center;
  color: rgba(0, 0, 0, 0.8);
  font-size: 1.125rem;
`;

const TapToStartButton = styled.div`
  display: grid;
  place-content: center;
  padding: 16px 24px;
  border-radius: 24px;
  border: 2px solid rgba(76, 99, 255, 0.5);
  background-color: rgba(0, 0, 0, 0.4);
  color: white;
  font-size: 24px;
`;

export default GptDriverDeviceProvider;
